import type { AppRouteContract } from '@beautyburst/routing';
import { adminBasePath } from './base-path.ts';
import type { AppBasePath } from '@beautyburst/tokens';

/**
 * Portal route contract. `phase` records the delivery phase that introduces the
 * screen so the roadmap stays visible in code.
 */
export interface RouteContract extends AppRouteContract {
  phase: number;
}

/** Admin console route contract. Sensitive screens additionally require MFA. */
export const ADMIN_ROUTES: readonly RouteContract[] = [
  { path: '/sign-in', screen: 'Administrator sign in', permission: null, phase: 1 },
  { path: '/', screen: 'Platform overview', permission: 'admin.overview.read', phase: 1 },
  {
    path: '/security',
    screen: 'Security',
    permission: 'admin.overview.read',
    phase: 7,
  },
  {
    path: '/retailers',
    screen: 'Retailer approvals',
    permission: 'admin.retailers.manage',
    phase: 2,
  },
  { path: '/users', screen: 'Users and roles', permission: 'admin.customers.manage', phase: 2 },
  {
    path: '/invitations',
    screen: 'Invitations',
    permission: 'admin.customers.manage',
    phase: 2,
  },
  {
    path: '/feature-flags',
    screen: 'Feature flags',
    permission: 'admin.feature_flags.manage',
    phase: 1,
  },
  { path: '/audit-log', screen: 'Audit log', permission: 'admin.audit.read', phase: 1 },
  { path: '/leads', screen: 'Lead review', permission: 'admin.leads.read', phase: 3 },
  {
    path: '/acquisition',
    screen: 'Acquisition and attribution',
    permission: 'admin.analytics.read',
    phase: 3,
  },
  {
    path: '/search-terms',
    screen: 'Search term insights',
    permission: 'admin.discovery.manage',
    flag: 'analytics.search_term_insights',
    phase: 3,
  },
  {
    path: '/memberships',
    screen: 'Beauty Burst+ memberships',
    permission: 'admin.membership.manage',
    flag: 'membership.program',
    requiresMfa: true,
    phase: 7,
  },
  {
    path: '/delivery-operations',
    screen: 'Delivery operations',
    permission: 'admin.delivery.manage',
    flag: 'delivery.tracking',
    phase: 6,
  },
  {
    path: '/direct-inventory',
    screen: 'Beauty Burst Direct inventory',
    permission: 'admin.direct_inventory.manage',
    flag: 'catalog.beauty_burst_direct',
    phase: 2,
  },
  {
    path: '/stores',
    screen: 'Stores oversight',
    permission: 'admin.retailers.read',
    flag: 'retailer.store_profile',
    phase: 2,
  },
  {
    path: '/imports',
    screen: 'Import oversight',
    permission: 'admin.imports.manage',
    flag: 'admin.import_oversight',
    requiresMfa: true,
    phase: 2,
  },
  { path: '/orders', screen: 'Order oversight', permission: 'admin.orders.read', phase: 5 },
  {
    path: '/refunds',
    screen: 'Operational exception handling',
    permission: 'admin.orders.manage',
    phase: 6,
  },
  { path: '/payouts', screen: 'Payout batches', permission: 'admin.payouts.manage', phase: 7 },
  {
    path: '/commissions',
    screen: 'Commission settings',
    permission: 'admin.commission.manage',
    phase: 7,
  },
  {
    path: '/markets',
    screen: 'Markets and service areas',
    permission: 'admin.service_areas.manage',
    requiresMfa: true,
    phase: 3,
  },
] as const;

export function resolveAdminRoute(
  path: string,
  basePath: AppBasePath = adminBasePath,
): RouteContract | undefined {
  return ADMIN_ROUTES.find((route) => route.path === basePath.strip(path));
}
